/**
 * Shared hook for Daily Menu Screen (landscape + portrait).
 * Fetches data from backend by customerCode, locationCode, menuId.
 * Listens to socket for real-time product availability changes.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { io } from "socket.io-client";
import {
  resolveLocaleValue,
  resolveLegend,
  resolveTicker,
} from "app/utils/resolveLocaleValue";
import { getDailyMenuScreenData } from "app/services/api/dailyMenu";
import { hardReloadScreen } from "app/pages/DailyMenuScreen/helpers/hardReloadScreen";
import {
  getDailyMenuRotationIntervalMs,
  getBackendBaseUrlFromEnv,
} from "app/helpers/dsok/dailyMenuUrl";
import allergensLegendData from "../../data/allergensLegend.json";
import { DAILY_MENU_TYPES } from "app/utils/constants/settings";

/** Placeholder obrázok pre polievky (bez obrázka). */
const SOUP_PLACEHOLDER_IMAGE = "/images/menu/soup-placeholder.png";
/** Placeholder obrázok pre hlavné jedlá (bez obrázka). */
const MEAL_PLACEHOLDER_IMAGE = "/images/menu/food-placeholder.png";

/** Interval pred automatickým obnovením stránky pri chybe "not-found" (ms). Počet sekúnd = COUNTDOWN_INTERVAL_MS / 1000. */
export const COUNTDOWN_INTERVAL_MS = 60000;

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

/** Plný zoznam alergénov (sk-SK, en-US) – načítaný z app/data/allergensLegend.json. */
const DEFAULT_ALLERGENS_LEGEND = allergensLegendData;

function sortByActiveFirst(items) {
  if (!Array.isArray(items) || items.length === 0) return items;
  const active = items.filter((i) => i?.isActive !== false);
  const inactive = items.filter((i) => i?.isActive === false);
  return [...active, ...inactive];
}

/**
 * Rovnaké podmienky zobrazovania pre polievky aj hlavné jedlá zo settings:
 * showUnavailableProducts (zobraziť/skryť nedostupné), sortUnavailableToEnd (aktívne prvé).
 * Filtrujeme nedostupné (isActive === false) len keď showUnavailableProducts je explicitne false.
 */
function applyDisplaySettings(
  items,
  showUnavailableProducts,
  sortUnavailableToEnd,
) {
  if (!Array.isArray(items)) return [];
  const hideUnavailable =
    showUnavailableProducts === false || showUnavailableProducts === "false";
  const filtered = hideUnavailable
    ? items.filter((i) => isProductActive(i))
    : items;
  const sorted = sortUnavailableToEnd ? sortByActiveFirst(filtered) : filtered;
  return sorted.map((item) => ({
    ...item,
    ordinal: item.displayNumber ?? item.ordinal ?? 0,
  }));
}

function isProductActive(item) {
  if (!item || typeof item !== "object") return true;
  const v = item.isActive ?? item.active;
  if (v === false || v === "false") return false;
  return true;
}

/**
 * Get current day key (monday..sunday) from today's date and menu weekStartDate.
 */
function getCurrentDayKey(weekStartDate) {
  if (!weekStartDate) return "monday";
  try {
    const start = new Date(weekStartDate + "T12:00:00");
    const today = new Date();
    const startDate = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    );
    const todayDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const diffDays = Math.floor(
      (todayDate - startDate) / (24 * 60 * 60 * 1000),
    );
    const index = Math.max(0, Math.min(6, diffDays));
    return DAYS[index];
  } catch {
    return "monday";
  }
}

/**
 * Normalize product for screen: allergens as array of numbers; title/description keys sk/en → sk-SK/en-US; isActive/active.
 */
function normalizeProduct(p) {
  if (!p || typeof p !== "object") return p;
  const out = { ...p };
  const activeRaw = out.isActive ?? out.active;
  if (activeRaw === false || activeRaw === "false") out.isActive = false;
  else if (activeRaw === true || activeRaw === "true") out.isActive = true;
  else if (out.isActive === undefined && out.active !== undefined)
    out.isActive = out.active === true || out.active === "true";
  if (Array.isArray(out.allergens) && out.allergens.length > 0) {
    const first = out.allergens[0];
    if (first != null && typeof first === "object" && "number" in first) {
      out.allergens = out.allergens
        .map((a) =>
          a && typeof a === "object" && Number.isFinite(a.number)
            ? a.number
            : null,
        )
        .filter((n) => n != null);
    }
  }
  if (
    out.title &&
    typeof out.title === "object" &&
    (out.title["sk"] != null || out.title["en"] != null)
  ) {
    out.title = {
      "sk-SK": out.title["sk-SK"] ?? out.title.sk ?? "",
      "en-US": out.title["en-US"] ?? out.title.en ?? "",
    };
  }
  if (
    out.description &&
    typeof out.description === "object" &&
    (out.description["sk"] != null || out.description["en"] != null)
  ) {
    out.description = {
      "sk-SK": out.description["sk-SK"] ?? out.description.sk ?? "",
      "en-US": out.description["en-US"] ?? out.description.en ?? "",
    };
  }
  return out;
}

/**
 * Normalize day.soups / day.meals to array of ids; support both [id, id, ...] and [{ id, isActive }, ...].
 * Returns { ids: string[], activeById: { [id]: boolean } }.
 */
function normalizeDayItemIds(raw) {
  if (!Array.isArray(raw) || raw.length === 0)
    return { ids: [], activeById: {} };
  const ids = [];
  const activeById = {};
  raw.forEach((item) => {
    if (item == null) return;
    const id = typeof item === "object" && "id" in item ? item.id : item;
    const idStr = id != null ? String(id) : "";
    if (!idStr) return;
    ids.push(idStr);
    if (typeof item === "object" && ("isActive" in item || "active" in item)) {
      const v = item.isActive ?? item.active;
      activeById[idStr] = v === true || v === "true";
    }
  });
  return { ids, activeById };
}

/**
 * Resolve soups/meals from day.soups / day.meals (pole id alebo objektov { id, isActive }) a productsById.
 */
function resolveItems(idsOrObjects, productsById) {
  const { ids, activeById } = Array.isArray(idsOrObjects)
    ? normalizeDayItemIds(idsOrObjects)
    : { ids: [], activeById: {} };
  if (!ids.length || !productsById || typeof productsById !== "object")
    return [];
  return ids
    .map((id, index) => {
      const p = productsById[id] ?? productsById[String(id)];
      if (!p) return null;
      const product = normalizeProduct({ ...p });
      if (activeById[id] !== undefined) product.isActive = activeById[id];
      product.displayNumber = index + 1;
      return product;
    })
    .filter(Boolean);
}

const SHORT_TO_FULL_LOCALE = {
  sk: "sk-SK",
  en: "en-US",
  cs: "cs-CZ",
  de: "de-DE",
};

/**
 * Build supportedLocales from settings.languages (object or string).
 */
function getSupportedLocales(settings) {
  if (!settings?.languages) return ["sk-SK"];
  const raw = settings.languages;
  if (typeof raw === "string" && raw.trim()) {
    const parts = raw
      .split("_")
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.map(
      (p) =>
        SHORT_TO_FULL_LOCALE[p] ??
        (p.length <= 3 ? `${p}-${p.toUpperCase()}` : p),
    );
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const keys = Object.keys(raw)
      .filter((k) => raw[k])
      .sort();
    return keys.length ? keys : ["sk-SK"];
  }
  return ["sk-SK"];
}

/** Normalizuje kód typu menu na neprázdny reťazec (alebo ""). */
function normalizeMenuTypeCode(v) {
  return v == null ? "" : String(v).trim();
}

/** "HH:MM" → počet minút od polnoci, alebo null. */
function timeToMinutes(hhmm) {
  if (typeof hhmm !== "string") return null;
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mi)) return null;
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

/** Je aktuálny čas (min) v okne [from, to)? Podpora aj cez polnoc (napr. 22:00–02:00). */
function isNowInWindow(nowMin, from, to) {
  const f = timeToMinutes(from);
  const t = timeToMinutes(to);
  if (f == null || t == null || f === t) return false;
  if (f < t) return nowMin >= f && nowMin < t;
  return nowMin >= f || nowMin < t; // okno cez polnoc
}

/** Je typ menu aktívny? (chýbajúci príznak = aktívny, kvôli spätnej kompatibilite). */
function isMenuTypeActive(cfg) {
  return !!cfg && cfg.active !== false;
}

/** Popisný názov typu menu z DAILY_MENU_TYPES podľa kódu. */
function getMenuTypeLabelByCode(code) {
  const found = DAILY_MENU_TYPES.find((x) => x.code === code);
  return found ? found.name : "";
}

/**
 * Vyberie zo zoznamu dnešných menu to, ktoré zodpovedá aktuálnemu času podľa
 * settings.menuTypes ({ breakfast:{from,to}, lunch:{...}, dinner:{...} }).
 *
 * 1) Ak aktuálny čas spadá do niektorého okna → daný typ.
 * 2) Ak je mimo všetkých okien (medzera, alebo pred/po prevádzke) → zobrazí sa
 *    NAJBLIŽŠÍ NASLEDUJÚCI typ podľa začiatku okna (from). Po poslednom okne sa
 *    prejde cez polnoc na prvé ranné menu.
 * 3) Ak nie sú nakonfigurované okná (alebo im nechýba menu) → prvé menu dňa.
 *
 * Do úvahy sa berú len typy, ktoré majú platné okno (from) aj menu na dnes.
 * @returns {{ menu: Object|null, menuTypeCode: string }}
 */
function selectMenuByTime(menus, menuTypes, now) {
  const list = Array.isArray(menus) ? menus.filter(Boolean) : [];
  if (list.length === 0) return { menu: null, menuTypeCode: "" };
  if (list.length === 1) {
    return { menu: list[0], menuTypeCode: normalizeMenuTypeCode(list[0].menuType) };
  }
  const nowMin = now.getHours() * 60 + now.getMinutes();

  if (menuTypes && typeof menuTypes === "object") {
    // 1) Aktuálny čas v niektorom okne (len aktívne typy).
    for (const { code } of DAILY_MENU_TYPES) {
      const cfg = menuTypes[code];
      if (isMenuTypeActive(cfg) && isNowInWindow(nowMin, cfg.from, cfg.to)) {
        const menu = list.find(
          (m) => normalizeMenuTypeCode(m.menuType) === code,
        );
        if (menu) return { menu, menuTypeCode: code };
      }
    }

    // 2) Mimo okien → najbližší nasledujúci typ podľa začiatku okna (from), len aktívne typy.
    const candidates = [];
    for (const { code } of DAILY_MENU_TYPES) {
      const cfg = menuTypes[code];
      if (!isMenuTypeActive(cfg)) continue;
      const fromMin = timeToMinutes(cfg.from);
      if (fromMin == null) continue;
      const menu = list.find((m) => normalizeMenuTypeCode(m.menuType) === code);
      if (!menu) continue;
      candidates.push({ code, fromMin, menu });
    }
    if (candidates.length > 0) {
      const byFrom = [...candidates].sort((a, b) => a.fromMin - b.fromMin);
      // Najbližší, ktorý ešte len príde; ak už žiadny nepríde, prejdi cez polnoc na prvý.
      const upcoming = byFrom.find((c) => c.fromMin >= nowMin) ?? byFrom[0];
      return { menu: upcoming.menu, menuTypeCode: upcoming.code };
    }
  }

  // 3) Fallback: prvé menu dňa.
  return { menu: list[0], menuTypeCode: normalizeMenuTypeCode(list[0].menuType) };
}

/**
 * Transform API response to screen data shape.
 * API by-code returns { code, status, dailyMenu, menus } with menus = pole menu (id, name, days, productsById, weekStartDate, menuType).
 * @param {Date} [now] - aktuálny čas pre výber typu menu (default new Date()).
 */
function transformResponse(response, menuId, settings, now = new Date(), orientation = "landscape") {
  const dailyMenu = response?.dailyMenu;
  const menus = response?.menus ?? (dailyMenu ? [dailyMenu] : []);
  const explicitMenu =
    menuId && menuId !== "default"
      ? dailyMenu && (dailyMenu.id === menuId || dailyMenu._id === menuId)
        ? dailyMenu
        : menus.find((m) => (m?.id ?? m?._id) === menuId)
      : null;
  const menuTypes = settings?.menuTypes ?? response?.settings?.menuTypes;
  const { menu: timeMenu, menuTypeCode } = selectMenuByTime(menus, menuTypes, now);
  const menu = explicitMenu ?? timeMenu ?? menus[0];
  if (!menu) {
    return null;
  }
  const activeMenuTypeCode =
    explicitMenu != null
      ? normalizeMenuTypeCode(explicitMenu.menuType)
      : menuTypeCode;
  const activeMenuType = activeMenuTypeCode
    ? { code: activeMenuTypeCode, label: getMenuTypeLabelByCode(activeMenuTypeCode) }
    : null;
  const productsById = menu?.productsById ?? {};
  const days = menu?.days ?? {};
  const dayKey = getCurrentDayKey(menu?.weekStartDate);
  const day = days[dayKey] ?? {};
  const soupIds = day?.soups ?? [];
  const mealIds = day?.meals ?? [];
  const soupsRaw = resolveItems(soupIds, productsById);
  const mealsRaw = resolveItems(mealIds, productsById);
  const rawShow = settings?.showUnavailableProducts;
  const showUnavailableProducts = rawShow !== false && rawShow !== "false";
  const sortUnavailableToEnd = settings?.sortUnavailableToEnd !== false;
  const soups = applyDisplaySettings(
    soupsRaw,
    showUnavailableProducts,
    sortUnavailableToEnd,
  );
  const meals = applyDisplaySettings(
    mealsRaw,
    showUnavailableProducts,
    sortUnavailableToEnd,
  );

  // Týždenný pohľad (LEN portrait): typ menu je konfigurovateľný v nastaveniach
  // (settings.weeklyMenuType = breakfast|lunch|dinner|night). Ak nie je nastavený
  // alebo preň neexistuje menu, použije sa aktuálne (podľa času) vybrané menu.
  const weeklyMenuTypeCode = normalizeMenuTypeCode(settings?.weeklyMenuType);
  const weeklyMenu =
    (weeklyMenuTypeCode &&
      menus.find(
        (m) => normalizeMenuTypeCode(m?.menuType) === weeklyMenuTypeCode,
      )) ||
    menu;
  const weeklyProductsById = weeklyMenu?.productsById ?? {};
  const weeklyDaysObj = weeklyMenu?.days ?? {};
  const weekStart = weeklyMenu?.weekStartDate
    ? new Date(weeklyMenu.weekStartDate + "T12:00:00")
    : null;
  const weekDays = DAYS.map((dk, idx) => {
    const d = weeklyDaysObj?.[dk] ?? {};
    const daySoups = applyDisplaySettings(
      resolveItems(d?.soups ?? [], weeklyProductsById),
      showUnavailableProducts,
      sortUnavailableToEnd,
    );
    const dayMeals = applyDisplaySettings(
      resolveItems(d?.meals ?? [], weeklyProductsById),
      showUnavailableProducts,
      sortUnavailableToEnd,
    );
    let dateObj = null;
    if (weekStart && !Number.isNaN(weekStart.getTime())) {
      dateObj = new Date(
        weekStart.getFullYear(),
        weekStart.getMonth(),
        weekStart.getDate() + idx,
      );
    }
    return { dayKey: dk, index: idx, date: dateObj, soups: daySoups, meals: dayMeals };
  }).filter((d) => d.soups.length + d.meals.length > 0);

  const weeklyRotationEnabled = settings?.weeklyMenuRotationEnabled === true;
  let weeklyRotationIntervalSec = Number(settings?.weeklyMenuRotationIntervalSec);
  if (!Number.isFinite(weeklyRotationIntervalSec) || weeklyRotationIntervalSec < 1) {
    weeklyRotationIntervalSec = 60;
  }
  const weeklyRotation = {
    enabled: weeklyRotationEnabled,
    intervalSec: weeklyRotationIntervalSec,
  };

  const defaultLocale = "sk-SK";
  const supportedLocales = getSupportedLocales(settings ?? response?.settings);
  let rotateMs = getDailyMenuRotationIntervalMs(settings ?? response?.settings);
  if (rotateMs <= 0 && supportedLocales.length >= 2) {
    rotateMs = 5000;
  }

  // Predvolený placeholder hlavného názvu (zobrazí sa, ak pre daný jazyk nie je
  // v nastaveniach obrazovky (settings.mainTitle) vyplnený vlastný text).
  // Kľúče v mainTitle môžu byť krátke ("sk", "en") aj plné ("sk-SK", "en-US") –
  // normalizujeme ich na plné, aby priamo prepísali defaultný záznam (inak by ho
  // resolveLocaleValue našiel skôr ako skrátený kľúč a override by sa neprejavil).
  const mainTitleSetting =
    settings?.mainTitle && typeof settings.mainTitle === "object"
      ? settings.mainTitle
      : {};
  const baseTitleObj = { "sk-SK": "Denné menu", "en-US": "Daily menu" };
  Object.keys(mainTitleSetting).forEach((code) => {
    const val = mainTitleSetting[code];
    if (typeof val !== "string" || !val.trim()) return;
    const fullCode =
      code.includes("-") ? code : (SHORT_TO_FULL_LOCALE[code] ?? code);
    baseTitleObj[fullCode] = val.trim();
  });

  // Poznámka: menu.name/response.title sa zámerne NEBERÚ do úvahy – menu.name sa
  // pri vytvorení každého menu automaticky vypĺňa rovnakým placeholder textom
  // (createNewMenu v dailyMenuAdmin.storage.js) a nie je nikde v UI editovateľné,
  // takže by vždy „tienilo“ toto globálne nastavenie. Jediný zdroj pravdy pre
  // hlavný názov na obrazovke je settings.mainTitle (s placeholderom v baseTitleObj).
  const titleObj = baseTitleObj;
  const tickerTexts =
    settings?.tickerTexts ?? response?.settings?.tickerTexts ?? {};
  const allergensLegend = response?.allergensLegend ?? DEFAULT_ALLERGENS_LEGEND;
  const showAllergens = settings?.showAllergens !== false;
  const showDescription = settings?.showDescription !== false;
  const showPrices = settings?.showPrices !== false;
  const displayTypeKey =
    orientation === "portrait"
      ? "productDisplayTypePortrait"
      : "productDisplayTypeLandscape";
  const rawDisplayType = (
    settings?.[displayTypeKey] ??
    settings?.productDisplayType ??
    "list"
  )
    .toString()
    .trim()
    .toLowerCase();
  const productDisplayType = rawDisplayType === "grid" ? "grid" : "list";

  return {
    menu,
    activeMenuType,
    settings: settings ?? response?.settings ?? {},
    defaultLocale,
    supportedLocales,
    rotateMs,
    soups,
    meals,
    titleObj,
    tickerTexts,
    allergensLegend,
    showAllergens,
    showDescription,
    showPrices,
    productDisplayType,
    weekDays,
    weeklyRotation,
  };
}

/**
 * Hook: fetch daily menu screen data from backend and expose in screen-ready shape.
 * @param {{ customerCode: string, locationCode: string, env?: string, menuId?: string }} params – from URL
 * @returns {{ loading, error, refetch, defaultLocale, supportedLocales, soups, meals, getTitle, getAllergensLegend, getTickerText, soupPlaceholderImage, mealPlaceholderImage }}
 */
export function useDailyMenuScreenData({
  customerCode,
  locationCode,
  env,
  menuId,
  orientation = "landscape",
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** Surové telo odpovede z API (kvôli výberu typu menu podľa aktuálneho času). */
  const [rawBody, setRawBody] = useState(null);
  /** Tik (každých 30 s) na prepočítanie aktívneho typu menu podľa času bez reloadu. */
  const [nowTick, setNowTick] = useState(0);
  /** Real-time availability overrides from socket (productId -> isActive). */
  const [availabilityPatches, setAvailabilityPatches] = useState({});
  const socketRef = useRef(null);

  const fetchData = useCallback(
    async (opts = {}) => {
      const { silent = false } = opts;
      if (!silent) {
        setError(null);
        setLoading(true);
        setAvailabilityPatches({});
      }
      try {
        const response = await getDailyMenuScreenData({
          customerCode,
          locationCode,
          env,
          menuId,
        });
        const body = response?.data ?? response;
        setRawBody(body);
        // Cache loga zákazníka – aby sa dalo zobraziť počas loadingu pri ďalších reloadoch.
        try {
          if (customerCode) {
            const lg = body?.settings?.logo;
            if (lg) localStorage.setItem(`dm-logo:${customerCode}`, lg);
            else localStorage.removeItem(`dm-logo:${customerCode}`);
          }
        } catch (_) {
          /* localStorage nemusí byť dostupný */
        }
        if (silent) setAvailabilityPatches({});
      } catch (e) {
        if (!silent) {
          setError(e?.message ?? "Failed to load daily menu");
          setRawBody(null);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [customerCode, locationCode, env, menuId],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Interval kontroly typu menu z nastavení (settings.menuTypeCheckIntervalMin, v minútach),
  // minimum 5 min (default 5).
  const menuTypeCheckIntervalMs = useMemo(() => {
    const min = Number(rawBody?.settings?.menuTypeCheckIntervalMin);
    const minutes = Number.isFinite(min) && min >= 5 ? min : 5;
    return minutes * 60 * 1000;
  }, [rawBody?.settings?.menuTypeCheckIntervalMin]);

  // Prepočet aktívneho typu menu podľa času (prepínanie raňajky/obed/večera).
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), menuTypeCheckIntervalMs);
    return () => clearInterval(id);
  }, [menuTypeCheckIntervalMs]);

  // Transformácia + výber menu podľa aktuálneho času (reaktívne na nové dáta aj na nowTick).
  const rawData = useMemo(() => {
    if (!rawBody) return null;
    const settings = rawBody?.settings ?? {};
    return transformResponse(rawBody, menuId, settings, new Date(), orientation);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nowTick zámerne spúšťa prepočet výberu podľa času
  }, [rawBody, menuId, nowTick, orientation]);

  useEffect(() => {
    if (!rawBody || !customerCode) return;
    // Pripojíme sa na room podľa customerId (backend emituje naň) aj podľa URL kódu.
    const rooms = [];
    if (rawBody?.customerId != null) {
      rooms.push(`customer:${String(rawBody.customerId)}`);
    }
    if (customerCode != null && !rooms.includes(`customer:${customerCode}`)) {
      rooms.push(`customer:${String(customerCode)}`);
    }
    if (rooms.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const environment = params.get("env") ?? env ?? "";
    // Base backendu berieme z env (funguje aj na verejnej obrazovke bez loginu),
    // nie z getBEUrl() – ten na verejnej obrazovke môže byť prázdny a socket by sa nepripojil.
    const base = getBackendBaseUrlFromEnv(environment);
    if (!base) return;
    const namespace = `${base}/daily-menu`;
    const socket = io(namespace, {
      path: "/socket.io",
      // polling prvý (funguje aj tam, kde websocket upgrade zlyháva), potom voliteľný upgrade
      transports: ["polling", "websocket"],
      reconnection: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      rooms.forEach((room) => socket.emit("join", { room }));
    });

    const handler = (payload) => {
      const id = payload?.productId;
      if (id == null) return;
      const raw = payload?.isActive;
      const isActive =
        raw === true || raw === "true"
          ? true
          : raw === false || raw === "false"
            ? false
            : undefined;
      if (isActive === undefined) return;
      setAvailabilityPatches((prev) => ({ ...prev, [id]: isActive }));
      // Refetch v pozadí, aby sme dostali aktuálne poradie produktov z backendu
      fetchData({ silent: true });
    };
    socket.on("daily-menu-product-availability-changed", handler);

    // Manuálny refresh z AdminUI – tvrdý reload (obíde cache na Androide/WebView,
    // aby sa prejavila zmena šablóny a načítal najnovší bundle).
    const refreshHandler = () => {
      hardReloadScreen();
    };
    socket.on("daily-menu-refresh", refreshHandler);

    return () => {
      socket.off("daily-menu-product-availability-changed", handler);
      socket.off("daily-menu-refresh", refreshHandler);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [rawBody, customerCode, fetchData]);

  const data = rawData;
  // Logo zákazníka – z aktuálnych dát, s fallbackom na cache (aby bolo k dispozícii aj počas loadingu).
  const cachedLogo = useMemo(() => {
    try {
      return customerCode
        ? localStorage.getItem(`dm-logo:${customerCode}`) || ""
        : "";
    } catch (_) {
      return "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rawBody: po fetchi sa cache aktualizuje
  }, [customerCode, rawBody]);
  const logo = data?.settings?.logo || cachedLogo || "";
  const defaultLocale = data?.defaultLocale ?? "sk-SK";
  const supportedLocales = data?.supportedLocales ?? [defaultLocale];
  const rotateMs = data?.rotateMs ?? 0;
  const showUnavailableProducts =
    data?.settings?.showUnavailableProducts !== false &&
    data?.settings?.showUnavailableProducts !== "false";
  const sortUnavailableToEnd = data?.settings?.sortUnavailableToEnd !== false;

  const soups = useMemo(() => {
    const base = data?.soups ?? [];
    const patched = base.map((item) => ({
      ...item,
      isActive:
        item?.id != null && availabilityPatches[item.id] !== undefined
          ? availabilityPatches[item.id]
          : item?.isActive,
    }));
    return applyDisplaySettings(
      patched,
      showUnavailableProducts,
      sortUnavailableToEnd,
    );
  }, [
    data?.soups,
    availabilityPatches,
    showUnavailableProducts,
    sortUnavailableToEnd,
  ]);

  const meals = useMemo(() => {
    const base = data?.meals ?? [];
    const patched = base.map((item) => ({
      ...item,
      isActive:
        item?.id != null && availabilityPatches[item.id] !== undefined
          ? availabilityPatches[item.id]
          : item?.isActive,
    }));
    return applyDisplaySettings(
      patched,
      showUnavailableProducts,
      sortUnavailableToEnd,
    );
  }, [
    data?.meals,
    availabilityPatches,
    showUnavailableProducts,
    sortUnavailableToEnd,
  ]);
  const titleObj = useMemo(
    () => data?.titleObj ?? { "sk-SK": "Denné menu", "en-US": "Daily menu" },
    [data?.titleObj],
  );
  const tickerTexts = useMemo(
    () => data?.tickerTexts ?? {},
    [data?.tickerTexts],
  );
  const allergensLegend = data?.allergensLegend ?? DEFAULT_ALLERGENS_LEGEND;
  const showAllergens = data?.showAllergens !== false;
  const showDescription = data?.showDescription !== false;
  const showPrices = data?.showPrices !== false;
  const rawDisplayType = (data?.productDisplayType ?? "list")
    .toString()
    .trim()
    .toLowerCase();
  const productDisplayType = rawDisplayType === "grid" ? "grid" : "list";

  const getTitle = useCallback(
    (locale) =>
      resolveLocaleValue(titleObj, locale, defaultLocale) || "Denné menu",
    [titleObj, defaultLocale],
  );
  const getAllergensLegend = useCallback(
    (locale) => resolveLegend(allergensLegend, locale, defaultLocale),
    [allergensLegend, defaultLocale],
  );
  const getTickerText = useCallback(
    (locale) => resolveTicker(tickerTexts, locale, defaultLocale),
    [tickerTexts, defaultLocale],
  );

  return {
    loading,
    error,
    refetch: fetchData,
    logo,
    data,
    activeMenuType: data?.activeMenuType ?? null,
    defaultLocale,
    supportedLocales,
    rotateMs,
    soups,
    meals,
    getTitle,
    getAllergensLegend,
    getTickerText,
    showAllergens,
    showDescription,
    showPrices,
    productDisplayType,
    weekDays: data?.weekDays ?? [],
    weeklyRotation: data?.weeklyRotation ?? { enabled: false, intervalSec: 60 },
    soupPlaceholderImage: SOUP_PLACEHOLDER_IMAGE,
    mealPlaceholderImage: MEAL_PLACEHOLDER_IMAGE,
  };
}

export default useDailyMenuScreenData;
